import React, { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Box, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import classNames from 'classnames';
import { getNextPeriod, getPreviousPeriod } from 'utils/date-range';
import { getDateTimeFromIso, MIN_SUPPORTED_DATE } from 'utils/date-time';
import { useMeContext } from 'modules/me';
import { DEFAULT_WEEKDAY, WeekDay } from 'modules/muiPickers/CustomLuxonUtils';
import { WeekPicker } from 'modules/common/components/WeekPicker';
import { CalendarDateWithToday } from 'modules/common/components/CalendarDate/CalendarDateWithToday';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessage } from 'modules/intl';

const useStyles = makeStyles((theme) => ({
  calendar: {
    marginRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
  },
  separator: {
    fontSize: theme.typography.h5.fontSize,
    margin: theme.spacing(0, 0.5),
    height: '100%'
  },
  chevrons: {
    display: 'flex'
  }
}));

const useDateStyles = makeStyles((theme) => ({
  inputField: {
    width: 'max-content',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    marginRight: ({ isTiwVpatEnabled }: { isTiwVpatEnabled: boolean }) =>
      isTiwVpatEnabled ? theme.spacing(0) : theme.spacing(-4)
  },
  input: {
    background: 'transparent',
    '-webkit-border-radius': theme.spacing(0.5),
    '&:hover': {
      outline: 'none',
      backgroundColor: 'transparent',
      border: ({ isTiwVpatEnabled }: { isTiwVpatEnabled: boolean }) =>
        isTiwVpatEnabled ? `solid 1px ${theme.palette.text.primary}` : 'none'
    },
    '&:focus': {
      outline: 'none',
      backgroundColor: 'transparent',
      border: ({ isTiwVpatEnabled }: { isTiwVpatEnabled: boolean }) =>
        isTiwVpatEnabled ? `solid 2px ${theme.palette.primary.main}` : 'none'
    }
  }
}));

const useNavigateStyles = makeStyles((theme) => ({
  root: {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.h5.fontWeight,
    color: theme.palette.common.black
  },
  disabled: {
    color: theme.palette.grey[400]
  }
}));

export type DateRange = {
  start: DateTime;
  end: DateTime;
};

type DateRangeSelectorProps = {
  selectedDateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  className?: string;
  showSingleDay?: boolean;
  readOnly?: boolean;
};

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  selectedDateRange: { end: endDate, start: startDate },
  onDateRangeChange,
  className,
  showSingleDay = false
}: DateRangeSelectorProps) => {
  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
  const classes = useStyles();
  const dateStyles = useDateStyles({ isTiwVpatEnabled });
  const navigateClasses = useNavigateStyles();

  const me = useMeContext();
  const weekDay = (me?.workWeekStartDay?.uri as WeekDay) || DEFAULT_WEEKDAY;

  const handleBeforeClick = useCallback(() => {
    const period = getPreviousPeriod(startDate, endDate);
    if (period?.start && period?.end) {
      onDateRangeChange({ start: period.start, end: period.end });
    }
  }, [startDate, endDate, onDateRangeChange]);

  const handleAfterClick = useCallback(() => {
    const period = getNextPeriod(startDate, endDate);
    if (period?.start && period?.end) {
      onDateRangeChange({ start: period.start, end: period.end });
    }
  }, [startDate, endDate, onDateRangeChange]);

  const onDateChange = useCallback(
    (date) => {
      onDateRangeChange({ start: date, end: date });
    },
    [onDateRangeChange]
  );

  const endOfAllowedRange = useMemo(() => {
    const end = getDateTimeFromIso(endDate);
    const start = getDateTimeFromIso(startDate);
    return start && end
      ? MIN_SUPPORTED_DATE >= start && MIN_SUPPORTED_DATE <= end
      : false;
  }, [endDate, startDate]);

  const onWeekChange = useCallback(
    (start: DateTime, end: DateTime) => {
      onDateRangeChange({ start, end });
    },
    [onDateRangeChange]
  );

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const prev = useMessage('timelineView.prev');
  const next = useMessage('timelineView.next');

  return (
    <Box
      className={classNames(classes.calendar, className)}
      data-testid="date-range-selector"
    >
      <Box className={classes.chevrons}>
        <IconButton
          size="small"
          onClick={handleBeforeClick}
          data-testid="previous-action-button"
          disabled={endOfAllowedRange}
          className={classNames(navigateClasses.root, {
            [navigateClasses.disabled]: endOfAllowedRange
          })}
          aria-label={isTiwScreenReaderEnabled ? prev : ''}
        >
          <NavigateBefore />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleAfterClick}
          data-testid="next-action-button"
          classes={navigateClasses}
          aria-label={isTiwScreenReaderEnabled ? next : ''}
        >
          <NavigateNext />
        </IconButton>
      </Box>
      {showSingleDay && startDate.toISODate() === endDate.toISODate() ? (
        <CalendarDateWithToday
          testId={'date-range'}
          classes={dateStyles}
          value={startDate}
          hasEndAdornment={false}
          handleDateChange={onDateChange}
          minDate={MIN_SUPPORTED_DATE}
          disableUnderline
          inputVariant="standard"
        />
      ) : (
        <WeekPicker
          value={startDate}
          handleDateChange={onWeekChange}
          startWeek={weekDay}
          minDate={MIN_SUPPORTED_DATE}
        />
      )}
    </Box>
  );
};
